import React, { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { Design } from "../../../../core/models/entities/Design";
import { CircularProgress, DialogActions } from "@mui/material";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";

interface DesignViewPopupProps {
  isOpen: boolean;
  onClose: () => void;
  design: Design;
  highlightLogoId: string;
}

interface LogoResource {
  image: HTMLImageElement;
  x: number;
  y: number;
  boxWidthPx: number;
  boxHeightPx: number;
  drawWidth: number;
  drawHeight: number;
  rotation: number;
  isTarget: boolean;
}

export function DesignViewPopup({
  isOpen,
  onClose,
  design,
  highlightLogoId,
}: DesignViewPopupProps) {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const canvasRef = useCallback((node: HTMLCanvasElement | null) => {
    if (node !== null) {
      setCanvas(node);
    }
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestRef = React.useRef<number>();
  const resourcesRef = React.useRef<{
    viewImage: HTMLImageElement;
    logos: LogoResource[];
  } | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (isOpen && highlightLogoId && canvas) {
      const loadResources = async () => {
        setIsLoading(true);
        setError(null);
        resourcesRef.current = null;

        try {
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            console.warn("Could not get 2d context");
            if (isMounted) setIsLoading(false);
            return;
          }

          // 1. Find the highlighted logo and its view
          const targetLogo = design.designLogos.find(
            (dl) => dl.inDesignId === highlightLogoId
          );

          if (!targetLogo) {
            console.warn("Target logo not found", highlightLogoId);
            if (isMounted) {
              setError("Logo not found in design");
              setIsLoading(false);
            }
            return;
          }

          const view = design.style?.styleViews.find(
            (v) => v.id === targetLogo.viewId
          );

          if (!view) {
            console.warn("View not found for logo", targetLogo.viewId);
            if (isMounted) {
              setError("View not found for logo");
              setIsLoading(false);
            }
            return;
          }

          // 2. Load the view image
          const viewImage = new Image();
          viewImage.crossOrigin = "anonymous";

          try {
            await new Promise((resolve, reject) => {
              viewImage.onload = resolve;
              viewImage.onerror = (e) => {
                console.error("View image load error", e);
                reject(e);
              };
              viewImage.src = view.imageUrl;
            });
          } catch (e) {
            if (isMounted) {
              console.error("Failed to load view image", e);
              setError("Failed to load view image");
              setIsLoading(false);
            }
            return;
          }

          if (!isMounted) return;

          // 3. Setup canvas dimensions matching the original image
          canvas.width = viewImage.naturalWidth;
          canvas.height = viewImage.naturalHeight;

          // 4. Prepare logos
          const logosOnView = design.designLogos.filter(
            (dl) => dl.viewId === view.id
          );

          const loadedLogos: LogoResource[] = [];

          for (const logo of logosOnView) {
            const logoUrl =
              logo.logo.displayImage?.digitUrl ||
              logo.logo.displayImage?.lowResUrl;
            if (!logoUrl) continue;

            const logoImg = new Image();
            logoImg.crossOrigin = "anonymous";

            try {
              await new Promise((resolve) => {
                logoImg.onload = resolve;
                logoImg.onerror = (e) => {
                  console.warn("Logo load error", logoUrl, e);
                  resolve(null); // Skip if fails
                };
                logoImg.src = logoUrl;
              });
            } catch {
              continue;
            }

            if (!isMounted) return;

            // Calculate dimensions and position
            const ppcm = view.ppcm || 17;
            const boxWidthPx = (logo.widthMm || 0) * (ppcm / 10);
            const boxHeightPx = (logo.heightMm || 0) * (ppcm / 10);

            // Calculate "contain" fit
            let drawWidth = boxWidthPx;
            let drawHeight = boxHeightPx;

            if (
              logoImg.naturalWidth > 0 &&
              logoImg.naturalHeight > 0 &&
              boxWidthPx > 0 &&
              boxHeightPx > 0
            ) {
              const imgAspect = logoImg.naturalWidth / logoImg.naturalHeight;
              const boxAspect = boxWidthPx / boxHeightPx;

              if (imgAspect > boxAspect) {
                drawWidth = boxWidthPx;
                drawHeight = boxWidthPx / imgAspect;
              } else {
                drawHeight = boxHeightPx;
                drawWidth = boxHeightPx * imgAspect;
              }
            }

            loadedLogos.push({
              image: logoImg,
              x: logo.logoX,
              y: logo.logoY,
              boxWidthPx,
              boxHeightPx,
              drawWidth,
              drawHeight,
              rotation: logo.angleRads || 0,
              isTarget: logo.inDesignId === highlightLogoId,
            });
          }

          resourcesRef.current = {
            viewImage,
            logos: loadedLogos,
          };
        } catch (err) {
          if (isMounted) {
            console.error("Error in loadResources", err);
            setError("An unexpected error occurred");
          }
        } finally {
          if (isMounted) setIsLoading(false);
        }
      };

      loadResources();
    }

    return () => {
      isMounted = false;
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [isOpen, highlightLogoId, canvas, design]);

  // Animation Loop
  useEffect(() => {
    if (!canvas || isLoading || !resourcesRef.current) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { viewImage, logos } = resourcesRef.current;

    const animate = (time: number) => {
      // Clear and draw background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(viewImage, 0, 0);

      // Draw all logos
      for (const logo of logos) {
        ctx.save();
        ctx.translate(
          logo.x + logo.boxWidthPx / 2,
          logo.y + logo.boxHeightPx / 2
        );
        ctx.rotate(logo.rotation);

        // Draw Logo Image
        ctx.drawImage(
          logo.image,
          -logo.drawWidth / 2,
          -logo.drawHeight / 2,
          logo.drawWidth,
          logo.drawHeight
        );

        // Draw Highlight if target
        if (logo.isTarget) {
          // Animation params
          const dashOffset = -time / 120; // Slower marching ants speed

          ctx.strokeStyle = "#00ff00"; // Solid green
          ctx.lineWidth = 4; // Thinner line
          ctx.setLineDash([6, 4]); // Striped
          ctx.lineDashOffset = dashOffset;

          // Padding
          // Use 5% of the average dimension, but at least 6px
          const padding = Math.max(
            6,
            ((logo.drawWidth + logo.drawHeight) / 2) * 0.05
          );

          const rw = logo.drawWidth + padding * 2;
          const rh = logo.drawHeight + padding * 2;
          const rx = -rw / 2;
          const ry = -rh / 2;
          const radius = Math.max(
            6,
            ((logo.drawWidth + logo.drawHeight) / 2) * 0.05
          ); // Rounded corner radius

          // Draw Rounded Rect
          ctx.beginPath();
          ctx.moveTo(rx + radius, ry);
          ctx.lineTo(rx + rw - radius, ry);
          ctx.quadraticCurveTo(rx + rw, ry, rx + rw, ry + radius);
          ctx.lineTo(rx + rw, ry + rh - radius);
          ctx.quadraticCurveTo(rx + rw, ry + rh, rx + rw - radius, ry + rh);
          ctx.lineTo(rx + radius, ry + rh);
          ctx.quadraticCurveTo(rx, ry + rh, rx, ry + rh - radius);
          ctx.lineTo(rx, ry + radius);
          ctx.quadraticCurveTo(rx, ry, rx + radius, ry);
          ctx.closePath();
          ctx.stroke();
        }

        ctx.restore();
      }

      requestRef.current = requestAnimationFrame(animate);
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [canvas, isLoading]); // Re-run when loading finishes

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex justify-between items-center">
        <span> </span>
        <IconButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
      </DialogTitle>
      <DialogContent className="flex justify-center items-center p-4 bg-white overflow-hidden">
        {isLoading && <CircularProgress />}
        {error && <div className="text-red-500">{error}</div>}
        <div
          style={{
            display: isLoading || error ? "none" : "flex",
            width: "100%",
            height: "100%",
            overflow: "auto",
            justifyContent: "center",
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: "100%",
              maxHeight: "700px",
              objectFit: "contain",
            }}
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ padding: "12px 24px" }}>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </DialogActions>
    </Dialog>
  );
}
